import { InputHTMLAttributes, ChangeEvent, FocusEvent, FormEvent, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import {
  defaultCountries,
  guessCountryByPartialPhoneNumber,
  parseCountry,
} from "react-international-phone";
import { BackButton, NextButton } from "src/components/application/styled-components/Buttons";
import {
  StyledErrorField,
  StyledTextField,
} from "src/components/application/styled-components/AddressFields";
import { nationalities } from "src/components/application/utils/nationalities";
import {
  AdhocKycAuth,
  AdhocKycContextResponse,
  AdhocKycUpdateDetailsBody,
  adhocKycErrorMessage,
  useAdhocKycUpdateDetailsMutation,
} from "src/store/features/api/adhocKyc";
import {
  AdhocKycBody,
  AdhocKycError,
  AdhocKycHeading,
  AdhocKycMatchNotice,
  ADHOC_KYC_NAVY,
} from "./AdhocKycShell";
import { PO_BOX_ERROR_MESSAGE, containsPoBox } from "src/utils/poBox";
import {
  FLAGGED_FIELD_HINT,
  flaggedFormFields,
  priorAttemptMessage,
  priorDetailsValues,
} from "./priorAttempt";

export interface UpdateDetailsFormProps {
  auth: AdhocKycAuth;
  context: AdhocKycContextResponse;
  /** Present when the customer chose to edit and can still go back to "confirm". */
  onBack?: () => void;
}

/** Flat form state; nested into `AdhocKycUpdateDetailsBody` on submit. */
export type UpdateDetailsValues = Record<string, string>;

const COUNTRY_OPTIONS: Array<{ value: "AU" | "NZ"; label: string }> = [
  { value: "AU", label: "Australia" },
  { value: "NZ", label: "New Zealand" },
];

/**
 * Dial codes come from the same `react-international-phone` catalogue the
 * onboarding and application phone fields use (`defaultCountries`), so the
 * three forms agree on which countries exist and what their codes are. AU and
 * NZ — the only residencies this campaign targets — sit at the top of the list.
 */
const PINNED_PHONE_COUNTRIES = ["au", "nz"];
const PHONE_COUNTRIES = defaultCountries.map(parseCountry);
const pinnedRank = (iso2: string): number => {
  const rank = PINNED_PHONE_COUNTRIES.indexOf(iso2);
  return rank === -1 ? PINNED_PHONE_COUNTRIES.length : rank;
};
export const PHONE_COUNTRY_OPTIONS: Array<{ value: string; label: string }> = [...PHONE_COUNTRIES]
  .sort((a, b) => pinnedRank(a.iso2) - pinnedRank(b.iso2))
  .map((c) => ({ value: c.iso2, label: `+${c.dialCode} ${c.name}` }));

const DEFAULT_PHONE_COUNTRY = "au";

/** The dial-code selection that matches the customer's residency ("NZ" → "nz"); AU when unknown. */
export const phoneCountryForResidency = (residencyCountry: string | undefined): string => {
  const iso2 = (residencyCountry || "").toLowerCase();
  return PHONE_COUNTRIES.some((c) => c.iso2 === iso2) ? iso2 : DEFAULT_PHONE_COUNTRY;
};

const dialCodeFor = (iso2: string): string =>
  PHONE_COUNTRIES.find((c) => c.iso2 === iso2)?.dialCode || "";

/** Digits only: the customer may type spaces, dashes or brackets between groups. */
const nationalDigits = (text: string): string => text.replace(/\D/g, "");

/**
 * Splits a stored number into the dial-code selection and the national part
 * the two inputs show. A `+`-prefixed value is matched against the catalogue
 * (the current selection breaks ties such as +1 US/CA); anything else is
 * treated as a national number under the current selection.
 */
export const splitPhoneNumber = (
  value: string,
  currentIso2: string,
): { iso2: string; national: string } => {
  const text = value.replace(/[^\d+]/g, "");
  if (!text.startsWith("+")) return { iso2: currentIso2, national: nationalDigits(text) };
  const { country, fullDialCodeMatch } = guessCountryByPartialPhoneNumber({
    phone: text,
    countries: defaultCountries,
    currentCountryIso2: currentIso2,
  });
  const digits = text.slice(1);
  if (!country || !fullDialCodeMatch) return { iso2: currentIso2, national: digits };
  return { iso2: country.iso2, national: digits.slice(country.dialCode.length) };
};

/**
 * What the two inputs show for the stored number. The form composes `phone`
 * from `phoneCountry`, so when the number carries that dial code the
 * selection is trusted as-is (a customer may pick US for a +1 number whose
 * area code reads as Canada); anything else is re-split from the number.
 */
export const displayedPhoneParts = (
  phone: string,
  iso2: string,
): { iso2: string; national: string } => {
  const dial = `+${dialCodeFor(iso2)}`;
  return phone.startsWith(dial)
    ? { iso2, national: phone.slice(dial.length) }
    : splitPhoneNumber(phone, iso2);
};

/** `("au", "412345678")` → `+61412345678`; an empty national number composes to "". */
export const composePhoneNumber = (iso2: string, national: string): string =>
  national ? `+${dialCodeFor(iso2)}${national}` : "";

/**
 * AU and NZ mobiles are written with a trunk `0` ("0412 …") that E.164 drops
 * (`+61412…`), and testers typed it every time. Only those two: elsewhere a
 * leading zero can be part of the national number.
 */
const TRUNK_ZERO_COUNTRIES = new Set(["au", "nz"]);
export const stripTrunkZero = (iso2: string, national: string): string =>
  TRUNK_ZERO_COUNTRIES.has(iso2) && national.startsWith("0") ? national.slice(1) : national;

export const PHONE_HINT = "Enter your mobile number without the country code (6 to 15 digits)";

const AU_STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];
const AU_STATE_OPTIONS = AU_STATES.map((s) => ({ value: s, label: s }));

/**
 * Document identifiers that are a fixed choice rather than free text. DataZoo
 * wants the licence's issuing state as the abbreviation ("NSW", not "New South
 * Wales"), so the form offers the same state list as the address section.
 */
export const DOCUMENT_FIELD_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {
  driversLicenceStateOfIssue: AU_STATE_OPTIONS,
};

const CARD_NUMBER_FIELD = "driversLicenceCardNo";
const STATE_OF_ISSUE_FIELD = "driversLicenceStateOfIssue";

/**
 * The licence CARD number (not the licence number) is the identifier Onfido
 * cannot read and the one a prod tester got wrong (CU074783, 2026-10-21):
 * every state prints it somewhere different, in a different shape. The
 * shapes are the DVS card-number rules; the locations are what the card
 * shows. Checked before submit so a typo never burns a DVS attempt.
 */
export const AU_LICENCE_CARD_NUMBER_RULES: Record<
  string,
  { pattern: RegExp; format: string; location: string }
> = {
  ACT: { pattern: /^[A-Z0-9]{10}$/, format: "10 letters or digits", location: "on the back" },
  NSW: { pattern: /^\d{10}$/, format: "10 digits", location: "on the front, top right" },
  NT: { pattern: /^\d{6,8}$/, format: "6 to 8 digits", location: "on the back" },
  QLD: { pattern: /^[A-Z0-9]{10}$/, format: "10 letters or digits", location: "on the back" },
  SA: { pattern: /^[A-Z0-9]{9}$/, format: "9 letters or digits", location: "on the back" },
  TAS: { pattern: /^[A-Z0-9]{9}$/, format: "9 letters or digits", location: "on the back" },
  VIC: { pattern: /^[A-Z0-9]{8}$/, format: "8 letters or digits", location: "on the back" },
  WA: {
    pattern: /^[A-Z0-9]{8,10}$/,
    format: "8 to 10 letters or digits",
    location: "on the front",
  },
};

/** Card numbers are printed in groups; DataZoo wants them bare and upper case. */
export const normaliseCardNumber = (value: string): string =>
  value.replace(/[\s-]/g, "").toUpperCase();

/** Where to find the card number and what it looks like for the chosen state; a prompt to pick the state first. */
export const cardNumberHint = (state: string | undefined): string => {
  const rule = AU_LICENCE_CARD_NUMBER_RULES[(state || "").toUpperCase()];
  return rule
    ? `Printed ${rule.location} of your ${state} licence — ${rule.format}. This is not your licence number.`
    : "Select the state of issue to see where the card number is printed. This is not your licence number.";
};

/** The error for a typed card number against the state's rule, or undefined when it fits (or the state is unknown). */
export const validateCardNumber = (
  state: string | undefined,
  value: string,
): string | undefined => {
  const rule = AU_LICENCE_CARD_NUMBER_RULES[(state || "").toUpperCase()];
  if (!rule) return undefined;
  return rule.pattern.test(normaliseCardNumber(value))
    ? undefined
    : `A ${state} card number is ${rule.format}`;
};

/**
 * DVS matches the given names as printed on the document, so a licence that
 * shows "Joseph Alan" fails when only "Joseph" is sent. The prod pilot lost
 * two of three checks exactly this way (2026-10-21): the middle name field was
 * optional and nobody filled it. It is now required unless the customer says
 * the document shows none.
 */
export const MIDDLE_NAME_HINT =
  "Enter your middle name(s) as printed on your ID, or tick the box if it shows none";

/**
 * Fields the form always renders. Anything the server lists in
 * `missingFields` that is NOT one of these is a document identifier (e.g. a
 * licence card number) and gets its own text input, sent back under
 * `documentIdentifiers`.
 */
const STANDARD_FIELDS = new Set([
  "firstName",
  "middleName",
  "lastName",
  "dateOfBirth",
  "phone",
  "address",
  "streetAddress",
  "city",
  "state",
  "postcode",
  "country",
  "primaryCitizenship",
  "secondaryCitizenship",
]);

const DOC_PREFIX = "doc:";

export const documentIdentifierFields = (missingFields: string[] | undefined): string[] =>
  (missingFields || []).filter((field) => !STANDARD_FIELDS.has(field));

/** `licenceCardNumber` → "Licence card number". */
export const humaniseField = (field: string): string => {
  const spaced = field
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/** Splits the held full name so the form starts with a sensible first/last. */
export const splitFullName = (fullName: string | undefined): { first: string; last: string } => {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return { first: parts[0] || "", last: parts.slice(1).join(" ") };
};

export const initialUpdateDetailsValues = (
  context: AdhocKycContextResponse,
): UpdateDetailsValues => {
  const held = context.heldDetails;
  const { first, last } = splitFullName(held?.fullName);
  // A retry starts from what the customer typed last time (document numbers
  // excepted — they are redacted after the check), so only the flagged
  // fields need touching.
  const prior = priorDetailsValues(context.priorAttempt?.details);
  const values: UpdateDetailsValues = {
    firstName: context.firstName || first,
    middleName: held?.middleName || "",
    noMiddleName: "",
    lastName: last,
    dateOfBirth: "",
    phone: "",
    phoneCountry: phoneCountryForResidency(context.residencyCountry),
    streetAddress: "",
    city: "",
    state: "",
    postcode: "",
    country: context.residencyCountry || "AU",
    primaryCitizenship: held?.citizenship || "",
    secondaryCitizenship: "",
    ...prior,
  };
  if (prior.phone) values.phoneCountry = splitPhoneNumber(prior.phone, values.phoneCountry).iso2;
  return values;
};

/**
 * The customer types their date of birth as DD/MM/YYYY (the AU/NZ convention —
 * a native date input renders in the browser's locale, which showed US order
 * to testers). The field opens a numeric keypad, which on most phones has no
 * slash, so `-`, `.` and a plain DDMMYYYY are accepted too (Bugbot, PR #3548).
 * Returns the ISO `YYYY-MM-DD` the API wants, or undefined when the text is
 * not a real calendar date in the past.
 */
export const dateOfBirthToIso = (value: string): string | undefined => {
  const text = value.trim();
  const match =
    text.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/) ?? text.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (!match) return undefined;
  const [, day, month, year] = match;
  const iso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  const date = new Date(`${iso}T00:00:00Z`);
  const time = date.getTime();
  // Reject rolled-over dates such as 31/02 (which `Date` silently turns into March).
  if (Number.isNaN(time) || date.toISOString().slice(0, 10) !== iso) return undefined;
  return time < Date.now() ? iso : undefined;
};

export const DATE_OF_BIRTH_HINT = "Enter your date of birth as DD/MM/YYYY";

const DOB_SEPARATOR = /[/.-]/;

/**
 * Keeps the date-of-birth input in DD/MM/YYYY shape while the customer types:
 * digits only, a slash inserted as each part fills ("3101" → "31/01"), a slash
 * the customer typed themselves kept ("31/" stays "31/"), ten characters at
 * most. Cheap impossibilities are rejected as they are typed — a day over 31
 * or a month over 12 (or either as 00) drops the offending digit. Pasted
 * values keep working: `-` and `.` separators are normalised to `/`, a bare
 * DDMMYYYY is split, and single-digit parts in front of a separator are
 * zero-padded ("1/2/1990" → "01/02/1990"). Whether the result is a real past
 * date is still `dateOfBirthToIso`'s call.
 */
export const formatDateOfBirthInput = (raw: string): string => {
  const text = raw.trim();
  const parts = text.split(DOB_SEPARATOR);
  const typedWithSeparators =
    parts.length > 1 && parts.length <= 3 && parts.every((part) => /^\d*$/.test(part));
  const padded = typedWithSeparators
    ? parts.map((part, i) => (part.length === 1 && i < parts.length - 1 ? `0${part}` : part))
    : [text.replace(/\D/g, "")];
  let digits = padded.join("").slice(0, 8);
  const day = digits.slice(0, 2);
  if (day.length === 2 && (day === "00" || Number(day) > 31)) digits = digits.slice(0, 1);
  const month = digits.slice(2, 4);
  if (month.length === 2 && (month === "00" || Number(month) > 12)) digits = digits.slice(0, 3);
  const endsWithSeparator = typedWithSeparators && parts[parts.length - 1] === "";
  let out = digits.slice(0, 2);
  if (digits.length > 2 || (digits.length === 2 && endsWithSeparator))
    out += `/${digits.slice(2, 4)}`;
  if (digits.length > 4 || (digits.length === 4 && endsWithSeparator))
    out += `/${digits.slice(4, 8)}`;
  return out;
};

/** The error for the mobile number, or undefined when it is a plausible national number. */
export const validatePhone = (values: UpdateDetailsValues): string | undefined => {
  const { national } = splitPhoneNumber(values.phone || "", values.phoneCountry || "");
  if (!national) return "Mobile number is required";
  return /^\d{6,15}$/.test(national) ? undefined : PHONE_HINT;
};

export const validateUpdateDetails = (
  values: UpdateDetailsValues,
  docFields: string[],
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const trimmed = (key: string): string => (values[key] || "").trim();
  if (!trimmed("firstName")) errors.firstName = "First name is required";
  if (!trimmed("middleName") && values.noMiddleName !== "true")
    errors.middleName = MIDDLE_NAME_HINT;
  if (!trimmed("lastName")) errors.lastName = "Last name is required";
  if (!dateOfBirthToIso(trimmed("dateOfBirth"))) errors.dateOfBirth = DATE_OF_BIRTH_HINT;
  const phoneError = validatePhone(values);
  if (phoneError) errors.phone = phoneError;
  if (!trimmed("streetAddress")) errors.streetAddress = "Street address is required";
  else if (containsPoBox(values.streetAddress)) errors.streetAddress = PO_BOX_ERROR_MESSAGE;
  if (!trimmed("city")) errors.city = "Suburb or city is required";
  if (values.country === "AU" && !trimmed("state")) errors.state = "State is required";
  if (!trimmed("postcode")) errors.postcode = "Postcode is required";
  docFields.forEach((field) => {
    if (!trimmed(DOC_PREFIX + field))
      errors[DOC_PREFIX + field] = `${humaniseField(field)} is required`;
  });
  if (docFields.includes(CARD_NUMBER_FIELD) && !errors[DOC_PREFIX + CARD_NUMBER_FIELD]) {
    const cardError = validateCardNumber(
      trimmed(DOC_PREFIX + STATE_OF_ISSUE_FIELD),
      trimmed(DOC_PREFIX + CARD_NUMBER_FIELD),
    );
    if (cardError) errors[DOC_PREFIX + CARD_NUMBER_FIELD] = cardError;
  }
  return errors;
};

const optional = (value: string): string | undefined => value.trim() || undefined;

/** MUI's `inputProps` is typed without `data-*` keys; the cast keeps the test id on the real input. */
const radioTestId = (kind: string): InputHTMLAttributes<HTMLInputElement> =>
  ({ "data-testid": `adhoc-kyc-document-${kind}` } as InputHTMLAttributes<HTMLInputElement>);

/**
 * CR3 — the document the form starts on: the one the server routed when it is
 * offered, else the first option. Empty when the campaign offers no choice.
 */
export const initialDocumentService = (context: AdhocKycContextResponse): string => {
  const options = context.documentOptions || [];
  return (options.find((o) => o.service === context.dvsService) || options[0])?.service || "";
};

export const toUpdateDetailsBody = (
  values: UpdateDetailsValues,
  docFields: string[],
  dvsService?: string,
): AdhocKycUpdateDetailsBody => ({
  firstName: values.firstName.trim(),
  middleName: values.noMiddleName === "true" ? undefined : optional(values.middleName),
  lastName: values.lastName.trim(),
  // Validated before this runs; the fallback only keeps the type honest for a direct caller.
  dateOfBirth: dateOfBirthToIso(values.dateOfBirth) || values.dateOfBirth.trim(),
  phone: values.phone.trim(),
  address: {
    streetAddress: values.streetAddress.trim(),
    city: values.city.trim(),
    state: optional(values.state),
    postcode: values.postcode.trim(),
    country: values.country as "AU" | "NZ",
  },
  primaryCitizenship: optional(values.primaryCitizenship),
  secondaryCitizenship: optional(values.secondaryCitizenship),
  documentIdentifiers: docFields.length
    ? Object.fromEntries(
        docFields.map((field) => [
          field,
          field === CARD_NUMBER_FIELD
            ? normaliseCardNumber(values[DOC_PREFIX + field])
            : values[DOC_PREFIX + field].trim(),
        ]),
      )
    : undefined,
  dvsService: dvsService || undefined,
});

interface FieldSpec {
  name: string;
  label: string;
  type?: string;
  inputMode?: "numeric" | "tel";
  options?: Array<{ value: string; label: string }>;
  half?: boolean;
  placeholder?: string;
}

const nationalityOptions = nationalities.map((n) => ({ value: n, label: n }));

const PERSON_FIELDS: FieldSpec[] = [
  { name: "firstName", label: "First name", half: true },
  // Rendered with the "no middle name" tick (see `renderMiddleNameField`).
  { name: "middleName", label: "Middle name(s) as shown on your ID", half: true },
  { name: "lastName", label: "Last name" },
  {
    name: "dateOfBirth",
    label: "Date of birth (DD/MM/YYYY)",
    inputMode: "numeric",
    placeholder: "DD/MM/YYYY",
  },
  // Rendered as the dial-code select + national-number pair (see `renderPhoneField`).
  { name: "phone", label: "Mobile number", type: "tel", inputMode: "tel" },
  { name: "primaryCitizenship", label: "Primary citizenship", options: nationalityOptions },
  {
    name: "secondaryCitizenship",
    label: "Second citizenship (optional)",
    options: nationalityOptions,
  },
];

const ADDRESS_FIELDS: FieldSpec[] = [
  { name: "streetAddress", label: "Street address" },
  { name: "city", label: "Suburb / city", half: true },
  { name: "postcode", label: "Postcode", half: true },
  { name: "country", label: "Country", options: COUNTRY_OPTIONS, half: true },
];

/**
 * The stale branch of step 2 — collects the customer's current details. What
 * we hold is masked, so only the name, citizenship and country prefill; the
 * rest is typed fresh. Submitting moves the request to `INFO_UPDATED`, after
 * which the server routes the customer to the Onfido re-check.
 */
export default function UpdateDetailsForm({
  auth,
  context,
  onBack,
}: UpdateDetailsFormProps): JSX.Element {
  const [values, setValues] = useState<UpdateDetailsValues>(() =>
    initialUpdateDetailsValues(context),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submit, { isLoading, error }] = useAdhocKycUpdateDetailsMutation();
  // CR3: with a document choice the identifiers follow the chosen document; without one
  // they are whatever the server listed as missing for the routed service.
  const documentOptions = context.documentOptions || [];
  const [documentService, setDocumentService] = useState(() => initialDocumentService(context));
  const chosenDocument = documentOptions.find((o) => o.service === documentService);
  const docFields = chosenDocument
    ? chosenDocument.identifierFields
    : documentIdentifierFields(context.missingFields);
  const insufficient = context.sufficient === false;
  const prior = context.priorAttempt;
  const flagged = flaggedFormFields(prior);
  // The hint follows the chosen document: the server's list names the routed service's identifiers.
  const missingHint = chosenDocument
    ? [...(context.missingFields || []).filter((f) => STANDARD_FIELDS.has(f)), ...docFields]
    : context.missingFields || [];

  const clearError = (name: string): void =>
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const { [name]: _dropped, ...rest } = prev;
      return rest;
    });

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = event.target;
    const next =
      name === "dateOfBirth"
        ? formatDateOfBirthInput(value)
        : name === DOC_PREFIX + CARD_NUMBER_FIELD
        ? normaliseCardNumber(value)
        : value;
    setValues((prev) => ({ ...prev, [name]: next }));
    clearError(name);
    // A state change can make an already-typed card number right or wrong.
    if (name === DOC_PREFIX + STATE_OF_ISSUE_FIELD) clearError(DOC_PREFIX + CARD_NUMBER_FIELD);
  };

  const handleNoMiddleNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const ticked = event.target.checked;
    setValues((prev) => ({ ...prev, noMiddleName: ticked ? "true" : "" }));
    clearError("middleName");
  };

  /** Blur validation for the two fields testers mistyped most: a wrong date or number is flagged before submit. */
  const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name } = event.target;
    if (name === "dateOfBirth" && values.dateOfBirth && !dateOfBirthToIso(values.dateOfBirth)) {
      setErrors((prev) => ({ ...prev, dateOfBirth: DATE_OF_BIRTH_HINT }));
    }
    if (name === "phone" && values.phone) {
      const phoneError = validatePhone(values);
      if (phoneError) setErrors((prev) => ({ ...prev, phone: phoneError }));
    }
    if (name === DOC_PREFIX + CARD_NUMBER_FIELD && values[name]) {
      const cardError = validateCardNumber(values[DOC_PREFIX + STATE_OF_ISSUE_FIELD], values[name]);
      if (cardError) setErrors((prev) => ({ ...prev, [name]: cardError }));
    }
  };

  const phoneParts = displayedPhoneParts(values.phone, values.phoneCountry);

  const handlePhoneCountryChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const iso2 = event.target.value;
    setValues((prev) => ({
      ...prev,
      phoneCountry: iso2,
      phone: composePhoneNumber(iso2, phoneParts.national),
    }));
    clearError("phone");
  };

  const handlePhoneNationalChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const typed = event.target.value;
    // A pasted "+61 412 345 678" picks its own dial code; anything else is national digits.
    const { iso2, national } = typed.trim().startsWith("+")
      ? splitPhoneNumber(typed, values.phoneCountry)
      : { iso2: values.phoneCountry, national: nationalDigits(typed) };
    setValues((prev) => ({
      ...prev,
      phoneCountry: iso2,
      phone: composePhoneNumber(iso2, stripTrunkZero(iso2, national)),
    }));
    clearError("phone");
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const nextErrors = validateUpdateDetails(values, docFields);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    submit({ auth, body: toUpdateDetailsBody(values, docFields, chosenDocument?.service) });
  };

  /**
   * The mobile number is two inputs — a dial-code select (same catalogue as the
   * onboarding phone field) and the national number — but one form value: the
   * E.164 string the API already takes, so the contract is unchanged.
   */
  const renderPhoneField = (spec: FieldSpec): JSX.Element => {
    const fieldError = errors.phone;
    return (
      <Grid item xs={12} key={spec.name}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <StyledTextField
            name="phoneCountry"
            label="Country code"
            value={phoneParts.iso2}
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "data-testid": "adhoc-kyc-field-phoneCountry" }}
            onChange={handlePhoneCountryChange}
            error={Boolean(fieldError)}
            selected
            sx={{ flex: "0 0 45%" }}
          >
            {PHONE_COUNTRY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </StyledTextField>
          <StyledTextField
            name="phone"
            label={spec.label}
            value={phoneParts.national}
            type={spec.type}
            placeholder="412 345 678"
            InputLabelProps={{ shrink: true }}
            inputProps={{ "data-testid": "adhoc-kyc-field-phone", inputMode: spec.inputMode }}
            onChange={handlePhoneNationalChange}
            onBlur={handleBlur}
            error={Boolean(fieldError)}
            selected={Boolean(phoneParts.national)}
            sx={{ flex: "1 1 auto" }}
          />
        </Stack>
        {fieldError ? <StyledErrorField role="alert">{fieldError}</StyledErrorField> : null}
      </Grid>
    );
  };

  /** "The issuer did not match this last time" under a field the prior attempt failed on. */
  const renderFlaggedHint = (name: string): JSX.Element | null =>
    flagged.has(name) ? (
      <Typography
        data-testid={`adhoc-kyc-flagged-${name}`}
        sx={{ fontSize: "12px", color: ADHOC_KYC_NAVY, fontWeight: 600, mt: 0.5 }}
      >
        {FLAGGED_FIELD_HINT}
      </Typography>
    ) : null;

  /** The middle name input plus the "my ID shows no middle name" tick that stands in for it. */
  const renderMiddleNameField = (spec: FieldSpec): JSX.Element => {
    const ticked = values.noMiddleName === "true";
    const fieldError = errors.middleName;
    return (
      <Grid item xs={12} md={6} key={spec.name}>
        <StyledTextField
          name="middleName"
          label={spec.label}
          value={ticked ? "" : values.middleName || ""}
          disabled={ticked}
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": "adhoc-kyc-field-middleName" }}
          onChange={handleChange}
          error={Boolean(fieldError)}
          selected={Boolean(values.middleName)}
        />
        {renderFlaggedHint("middleName")}
        <FormControlLabel
          sx={{ mt: 0.5 }}
          label={
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              My ID shows no middle name
            </Typography>
          }
          control={
            <Checkbox
              size="small"
              checked={ticked}
              onChange={handleNoMiddleNameChange}
              inputProps={radioTestId("no-middle-name")}
            />
          }
        />
        {fieldError ? <StyledErrorField role="alert">{fieldError}</StyledErrorField> : null}
      </Grid>
    );
  };

  const renderField = (spec: FieldSpec): JSX.Element => {
    if (spec.name === "phone") return renderPhoneField(spec);
    if (spec.name === "middleName") return renderMiddleNameField(spec);
    const value = values[spec.name] || "";
    const fieldError = errors[spec.name];
    const hint =
      spec.name === DOC_PREFIX + CARD_NUMBER_FIELD
        ? cardNumberHint(values[DOC_PREFIX + STATE_OF_ISSUE_FIELD])
        : undefined;
    return (
      <Grid item xs={12} md={spec.half ? 6 : 12} key={spec.name}>
        <StyledTextField
          name={spec.name}
          label={spec.label}
          value={value}
          type={spec.type}
          placeholder={spec.placeholder}
          select={Boolean(spec.options)}
          SelectProps={spec.options ? { native: true } : undefined}
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": `adhoc-kyc-field-${spec.name}`, inputMode: spec.inputMode }}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(fieldError)}
          selected={Boolean(value)}
        >
          {spec.options ? <option value="" /> : null}
          {spec.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </StyledTextField>
        {renderFlaggedHint(spec.name)}
        {hint ? (
          <Typography
            data-testid="adhoc-kyc-card-number-hint"
            sx={{ fontSize: "12px", color: "#6B7280", mt: 0.5 }}
          >
            {hint}
          </Typography>
        ) : null}
        {fieldError ? <StyledErrorField role="alert">{fieldError}</StyledErrorField> : null}
      </Grid>
    );
  };

  const stateField: FieldSpec =
    values.country === "AU"
      ? {
          name: "state",
          label: "State",
          half: true,
          options: AU_STATES.map((s) => ({ value: s, label: s })),
        }
      : { name: "state", label: "Region (optional)", half: true };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit}
      data-testid="adhoc-kyc-update-details-form"
    >
      <AdhocKycHeading>
        {prior
          ? "Check and resubmit your details"
          : insufficient
          ? "We need a few more details"
          : "Update your details"}
      </AdhocKycHeading>
      {prior ? (
        <Box
          role="note"
          data-testid="adhoc-kyc-prior-attempt"
          sx={{
            border: "1px solid #FC4C4C",
            borderRadius: "12px",
            bgcolor: "#FFF5F5",
            p: 1.5,
            mb: 2,
            fontSize: "14px",
            color: ADHOC_KYC_NAVY,
          }}
        >
          {priorAttemptMessage(prior)}
        </Box>
      ) : null}
      <AdhocKycBody>
        {prior
          ? prior.reusesIdCheck
            ? "Correct anything that doesn't match your ID and resubmit. We'll check it with the issuer straight away — you won't need to repeat the ID check."
            : "Correct anything that doesn't match your ID and resubmit. Because your details are changing, we'll also ask you to complete a quick ID check on the next step."
          : insufficient
          ? "We don't hold everything needed to complete this check, so please fill in your current details below."
          : "Please enter your current details. Because these differ from what we hold, we'll also ask you to complete a quick ID check on the next step."}
      </AdhocKycBody>
      <AdhocKycMatchNotice />
      {missingHint.length ? (
        <Typography
          data-testid="adhoc-kyc-missing-fields"
          sx={{ fontSize: "13px", color: "#6B7280", mb: 2 }}
        >
          Missing: {missingHint.map(humaniseField).join(", ")}
        </Typography>
      ) : null}

      <Grid container spacing={2}>
        {PERSON_FIELDS.map(renderField)}
        <Grid item xs={12}>
          <Typography sx={{ fontWeight: 600, fontSize: "14px", mt: 1 }}>
            Residential address
          </Typography>
        </Grid>
        {ADDRESS_FIELDS.map(renderField)}
        {renderField(stateField)}
        {docFields.length || documentOptions.length > 1 ? (
          <Grid item xs={12}>
            <Typography sx={{ fontWeight: 600, fontSize: "14px", mt: 1 }}>
              Your ID document
            </Typography>
          </Grid>
        ) : null}
        {documentOptions.length > 1 ? (
          <Grid item xs={12}>
            <Typography sx={{ fontSize: "14px", color: "#6B7280", mb: 0.5 }}>
              Which document should we check?
            </Typography>
            <RadioGroup
              row
              name="dvsService"
              value={documentService}
              onChange={(event) => setDocumentService(event.target.value)}
              data-testid="adhoc-kyc-document-choice"
            >
              {documentOptions.map((option) => (
                <FormControlLabel
                  key={option.service}
                  value={option.service}
                  label={option.label}
                  control={<Radio inputProps={radioTestId(option.kind)} />}
                />
              ))}
            </RadioGroup>
          </Grid>
        ) : null}
        {docFields.map((field) =>
          renderField({
            name: DOC_PREFIX + field,
            label: humaniseField(field),
            options: DOCUMENT_FIELD_OPTIONS[field],
          }),
        )}
      </Grid>

      {error ? (
        <Box sx={{ mt: 2 }}>
          <AdhocKycError message={adhocKycErrorMessage(error)} />
        </Box>
      ) : null}

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: 3 }}>
        {onBack ? (
          <BackButton fullWidth onClick={onBack} data-testid="adhoc-kyc-update-back">
            Back
          </BackButton>
        ) : null}
        <NextButton
          fullWidth
          type="submit"
          disabled={isLoading}
          data-testid="adhoc-kyc-update-submit"
        >
          {prior ? "Resubmit" : "Save and continue"}
        </NextButton>
      </Stack>
    </Box>
  );
}
